"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React from "react"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import {
  ButtonWithArrowTall,
  colorPalletteIndex,
  colorPalletteIndexType,
} from "../common/bit/ButtonWithArrow"
import EnhancedTypography from "../common/bit/EnhancedTypography"
import { colors, mediaQueries, sizes } from "../theme/theme"

interface ImageCardProps {
  index: number
  data: CardProps
  desktopCardHeight?: string
}

export interface CardProps {
  title: string
  image: { url: string }
  content: string
  description: string
  category: string[]
  link: {
    url: string
    target: any
    name: React.JSX.Element
    text: React.JSX.Element
    href: string
  }
}

const Card = styled("a", {
  shouldForwardProp: (prop) =>
    prop !== "activeSwiper" &&
    prop !== "desktopCardHeight" &&
    prop !== "hoverColor",
})<{ activeSwiper?: boolean; desktopCardHeight?: string; hoverColor?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: ${(prop) =>
    prop.desktopCardHeight ? prop.desktopCardHeight : "unset"};
  transition: all 0.3s ease-in-out;
  background: none;
  padding: ${sizes.baseSingle};
  margin-bottom: ${sizes.baseQuadric};
  border-radius: ${sizes.borderRadius};
  text-decoration: none;
  ${mediaQueries.mobile} {
    width: 340px;
    height: 320px;
  }
  ${mediaQueries.tablet} {
    width: 340px;
    height: 320px;
  }
  ${mediaQueries.desktopM} {
    width: 276px;
    height: 400px;
    padding: ${sizes.baseSingle};
  }
  ${mediaQueries.desktopXl} {
    width: 360px;
    height: 400px;
  }
  &:hover {
    border: none;
    background: none;
    p {
      background-color: ${(props) => props.hoverColor};
      color: ${colors.white};
    }
    img {
      transform: scale(1.02);
    }
  }
  img {
    transition: all 0.3s ease-in-out;
  }
`
const BubbleWrapper = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  gap: ${sizes.baseSingle};
  z-index: 2;
  padding: ${sizes.baseSingle};
  p {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    border-radius: 30px;
    background-color: rgba(255, 255, 255, 0.8);
    color: ${colors.black};
    padding: 2px 12px;
  }
`
const StyledImage = styled(Image)`
  object-fit: cover;
  border-radius: ${sizes.borderRadius};
`
const StyledDescription = styled(EnhancedTypography)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;
  line-height: 19px;
  font-size: 14px;
  text-align: start;
`

const StyledButtonWithArrowTall = styled(ButtonWithArrowTall)`
  margin-top: auto;
  margin-bottom: ${`-${sizes.baseQuadric}`};
  height: 91px;
  padding: ${sizes.baseDouble};
  justify-content: space-between;
  ${mediaQueries.mobile} {
    width: 90%;
  }
  ${mediaQueries.desktopM} {
    padding: ${sizes.baseDouble};
    width: 250px;
  }
  ${mediaQueries.desktopXl} {
    width: 300px;
  }
`

const ImageCard: React.FC<ImageCardProps> = ({
  data,
  index,
  desktopCardHeight,
}) => {
  const { title, image, category, link } = data
  const colorPallette = colorPalletteIndex[
    index % colorPalletteIndex.length
  ] as colorPalletteIndexType
  const t = useTranslations("SEO")

  return (
    <Card
      href={getNonUmbracoUrl(link?.href || "/")}
      target={link?.target ? link?.target : "_self"}
      key={`image-card-item-${title}-${index + 1}`}
      desktopCardHeight={desktopCardHeight}
      hoverColor={colors[colorPallette]}
    >
      {image && (
        <StyledImage
          src={getUmbracoMediaUrl(image.url)}
          alt={`${t("ArticleAlt")}${title}`}
          fill
        />
      )}
      <BubbleWrapper>
        {category &&
          Array.from(
            new Set(
              category
                .filter((item) => item && item.trim() !== "") // Filter out empty or whitespace-only strings
                .map((item) => item.toLowerCase()),
            ),
          ).map((uniqueItem: string) => {
            // Find the original casing version of the category item
            const originalItem = category.find(
              (catItem) => catItem?.toLowerCase() === uniqueItem,
            )
            return (
              <Typography
                key={`image-card-bubble-${uniqueItem}-${index + 1}`}
                variant="body1"
              >
                {originalItem?.toUpperCase()}
              </Typography>
            )
          })}
      </BubbleWrapper>
      <StyledButtonWithArrowTall
        colorPallette={colorPallette}
        variant="elevated"
        sx={{ padding: `${sizes.baseDouble} ${sizes.baseDouble}` }}
      >
        {link?.text && (
          <StyledDescription variant="body2" component="h3">
            {link?.text || title}
          </StyledDescription>
        )}
      </StyledButtonWithArrowTall>
    </Card>
  )
}
export default ImageCard
